import axiosInstance from './axiosInstance';
import { API_ENDPOINTS } from '../config/api.config';

const { STOCKS } = API_ENDPOINTS;

export const stockApi = {
  /** GET /admin/stocks/ */
  getList: () => axiosInstance.get(STOCKS.LIST),

  /** GET /admin/stocks/{id}/ */
  getDetail: (id) => axiosInstance.get(STOCKS.DETAIL(id)),

  /** POST /admin/stocks/ */
  create: (data) => axiosInstance.post(STOCKS.LIST, data),

  /** PATCH /admin/stocks/{id}/ */
  update: (id, data) => axiosInstance.patch(STOCKS.DETAIL(id), data),

  /** DELETE /admin/stocks/{id}/ — requires password in body */
  remove: (id, password) => axiosInstance.delete(STOCKS.DETAIL(id), { data: { password } }),

  /** GET /admin/stocks/car-catalog/ */
  getCarCatalog: () => axiosInstance.get(STOCKS.CAR_CATALOG),

  // Investment recovery — sales recorded out of one stock car
  /** GET /admin/stocks/{id}/sales/ → { pnl } */
  getSales: (id) => axiosInstance.get(STOCKS.SALES(id)),
  /** POST /admin/stocks/{id}/sales/ { item_name, amount, sold_at?, note? } */
  addSale: (id, data) => axiosInstance.post(STOCKS.SALES(id), data),
  /** DELETE /admin/stocks/{id}/sales/{saleId}/ */
  removeSale: (id, saleId) => axiosInstance.delete(STOCKS.SALE(id, saleId)),
};

// Outside parts — N units of the SAME part bought up front and held on the
// shelf. Stocked WITH the car they fit, so inquiries match them automatically
// and the supervisor is offered them as a source next to OSM and CAP.
export const outsidePartApi = {
  /** GET /admin/stocks/outside-parts/?search=&in_stock=1&active=1 */
  getList: (params = {}) => axiosInstance.get(STOCKS.OUTSIDE_PARTS, { params }),
  /** GET /admin/stocks/outside-parts/{id}/ → batch + pnl + sales */
  getDetail: (id) => axiosInstance.get(STOCKS.OUTSIDE_PART(id)),
  /** POST /admin/stocks/outside-parts/ */
  create: (data) => axiosInstance.post(STOCKS.OUTSIDE_PARTS, data),
  /** PATCH /admin/stocks/outside-parts/{id}/ */
  update: (id, data) => axiosInstance.patch(STOCKS.OUTSIDE_PART(id), data),
  /** DELETE /admin/stocks/outside-parts/{id}/ — only while nothing is sold.
      { reason? } goes to the deletion ledger. */
  remove: (id, data = {}) => axiosInstance.delete(STOCKS.OUTSIDE_PART(id), { data }),
  /** POST /admin/stocks/outside-parts/{id}/sales/ { quantity, amount, sold_at?, customer_name?, note? } */
  addSale: (id, data) => axiosInstance.post(STOCKS.OUTSIDE_PART_SALES(id), data),
  /** DELETE /admin/stocks/outside-parts/{id}/sales/{saleId}/ — manual rows only */
  removeSale: (id, saleId) => axiosInstance.delete(STOCKS.OUTSIDE_PART_SALE(id, saleId)),
};

// Trade-ins — old parts CAP took in on an exchange (resell / dismantle / scrap)
export const tradeInApi = {
  /** GET /admin/stocks/trade-ins/?disposition=&q= */
  getList: (params = {}) => axiosInstance.get(STOCKS.TRADE_INS, { params }),
  /** PATCH /admin/stocks/trade-ins/{id}/ { disposition, received, name, note } */
  update: (id, data) => axiosInstance.patch(STOCKS.TRADE_IN(id), data),
  /** POST /admin/stocks/trade-ins/{id}/sales/ { item_name, amount, sold_at?, note? } */
  addSale: (id, data) => axiosInstance.post(STOCKS.TRADE_IN_SALES(id), data),
  /** DELETE /admin/stocks/trade-ins/{id}/sales/{saleId}/ */
  removeSale: (id, saleId) => axiosInstance.delete(STOCKS.TRADE_IN_SALE(id, saleId)),
};

const { OSM } = API_ENDPOINTS;

export const osmApi = {
  /** GET /admin/osm/?bucket=&stage=&q= */
  getList: ({ bucket = '', stage = 'won', q = '' } = {}) =>
    axiosInstance.get(OSM.LIST, {
      params: { bucket: bucket || undefined, stage, q: q || undefined },
    }),
  /** PATCH /admin/osm/{partId}/ { actual_price, settled, note, supervisor_notes } */
  set: (partId, data) => axiosInstance.patch(OSM.SET(partId), data),
  /** POST /admin/osm/{partId}/payments/ { amount, paid_at?, note? } — one advance */
  addPayment: (partId, data) => axiosInstance.post(OSM.PAYMENTS(partId), data),
  /** DELETE /admin/osm/{partId}/payments/{paymentId}/ */
  removePayment: (partId, paymentId) => axiosInstance.delete(OSM.PAYMENT(partId, paymentId)),
  /** POST /admin/osm/{partId}/unavailable/ { reason } — couldn't procure, refund customer */
  markUnavailable: (partId, data) => axiosInstance.post(OSM.UNAVAILABLE(partId), data),
  /** DELETE /admin/osm/{partId}/unavailable/ — undo (only if refund still unpaid) */
  restore: (partId) => axiosInstance.delete(OSM.UNAVAILABLE(partId)),
};
